import { useEffect, useState, useSyncExternalStore } from 'react';

import { useGetGamePanelQuery } from '@/api/queries/game';
import { useAppSelector } from '@/redux/store';

/** Backend `SCRATCH_MIN_LEVEL` — scratch endpoints 403 below it. */
const SCRATCH_MIN_LEVEL = 7;

const SEEN_KEY = 'scratch_seen_event_end_v1';
const PANEL_POLL_MS = 10 * 60 * 1000;
const TICK_MS = 30 * 1000;

const listeners = new Set<() => void>();

function readSeenEndMs(): number | null {
  try {
    const raw = localStorage.getItem(SEEN_KEY);

    return raw ? Number(raw) : null;
  } catch {
    return null;
  }
}

/**
 * Remember that the player opened the scratch page during the event that
 * ends at `endAt` — the attention highlight goes quiet until the next one.
 */
export function markScratchEventSeen(endAt: string | null | undefined): void {
  const endMs = endAt ? new Date(endAt).getTime() : NaN;

  if (!Number.isFinite(endMs) || readSeenEndMs() === endMs) return;

  try {
    localStorage.setItem(SEEN_KEY, String(endMs));
  } catch {
    /* localStorage unavailable — the highlight just stays on. */
  }
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);

  return () => listeners.delete(listener);
}

interface ScratchLiveState {
  /** Event window is open right now and the player may play. */
  isLive: boolean;
  /** Live and the player hasn't opened the scratch page this event. */
  isUnseen: boolean;
}

/**
 * Whether a scratch event is running, from the shared /game/panel query.
 * Re-evaluated on a short tick so the highlight flips at 00:00 UTC without
 * a refetch; the panel itself is re-polled every 10 minutes.
 */
export function useScratchLive(): ScratchLiveState {
  const level = useAppSelector((s) => s.user.user?.level ?? 0);
  const eligible = level >= SCRATCH_MIN_LEVEL;
  const { data } = useGetGamePanelQuery(undefined, {
    skip: !eligible,
    pollingInterval: PANEL_POLL_MS,
  });
  const seenEndMs = useSyncExternalStore(subscribe, readSeenEndMs);
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    if (!eligible) return undefined;
    const id = setInterval(() => setNowMs(Date.now()), TICK_MS);

    return (): void => clearInterval(id);
  }, [eligible]);

  const scratch = data?.scratch;
  let endMs: number | null = null;

  if (scratch?.status === 'active' && scratch.endAt) {
    endMs = new Date(scratch.endAt).getTime();
  } else if (
    scratch?.status === 'pending' &&
    scratch.startAt &&
    new Date(scratch.startAt).getTime() <= nowMs
  ) {
    // Started since the panel was fetched; windows last 24h.
    endMs = new Date(scratch.startAt).getTime() + 24 * 60 * 60 * 1000 - 1000;
  }

  const isLive = eligible && endMs !== null && nowMs < endMs;

  return { isLive, isUnseen: isLive && seenEndMs !== endMs };
}
